import { useState, useEffect, useRef } from "react";
import { Box, Typography, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import { SHORTCUT_GROUPS } from "../../shortcuts";

// Other components open the cheat-sheet by dispatching this window event
// (mirrors the "hw-start-tour" pattern used for the walkthrough).
export const SHORTCUTS_EVENT = "hw-open-shortcuts";

// Keyboard-shortcut UI is only offered with a mouse or trackpad (and so, nearly always, a
// keyboard). Width can't tell: a phone in landscape is as wide as a small laptop window.
export const KEYBOARD_UI_QUERY = "(hover: hover) and (pointer: fine)";
export const hasKeyboardUI = () => window.matchMedia(KEYBOARD_UI_QUERY).matches;

const primary = "#1535BD";

const keycapSx = {
  fontFamily: "monospace",
  fontWeight: 700,
  fontSize: "0.8rem",
  lineHeight: 1,
  color: primary,
  bgcolor: "rgba(21,53,189,0.1)",
  border: "1px solid rgba(21,53,189,0.18)",
  borderRadius: "6px",
  px: 0.85,
  py: 0.5,
  minWidth: 16,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

/**
 * Desktop keyboard-shortcuts cheat-sheet. Renders nothing visible until opened —
 * either via the global "?" key or the "hw-open-shortcuts" window event (sidebar item).
 */
const KeyboardShortcuts = () => {
  const [open, setOpen] = useState(false);

  // Read the latest value inside the global handler without re-binding on every render.
  const openRef = useRef(open);
  openRef.current = open;

  // "?" toggles the cheat-sheet. "?" is Shift+/, and useSearchHotkeys keys on
  // e.key === "/", so there is no collision with the search shortcut. Not gated on
  // KEYBOARD_UI_QUERY: outside a text field only a hardware keyboard can type "?" (a soft
  // keyboard is only up for a text field), e.g. on a touch tablet with a keyboard case.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "?") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Toggle closed if it's already showing.
      if (openRef.current) {
        e.preventDefault();
        setOpen(false);
        return;
      }
      // Don't steal "?" while typing in a field or while another modal/dialog is open.
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (el && el.isContentEditable)) return;
      // (A closed keepMounted modal, e.g. a SwipeableDrawer, stays in the DOM as MuiModal-hidden.)
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden), [role="dialog"]')) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Opened from elsewhere (the sidebar item).
  useEffect(() => {
    const openHandler = () => {
      if (hasKeyboardUI()) setOpen(true);
    };
    window.addEventListener(SHORTCUTS_EVENT, openHandler);
    return () => window.removeEventListener(SHORTCUTS_EVENT, openHandler);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", mt: "env(safe-area-inset-top, 24px)" } }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(21,53,189,0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <KeyboardIcon sx={{ color: primary, fontSize: 20 }} />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.05rem",
              color: primary,
              fontFamily: "Mulish, sans-serif",
              letterSpacing: "-0.01em",
            }}
          >
            Keyboard shortcuts
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={() => setOpen(false)} sx={{ color: "rgba(0,0,0,0.35)", mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 3, px: 3 }}>
        {SHORTCUT_GROUPS.map((group, gi) => (
          <Box key={group.context} sx={{ mb: gi === SHORTCUT_GROUPS.length - 1 ? 0 : 2.5 }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: primary,
                opacity: 0.6,
                fontFamily: "Mulish, sans-serif",
                mb: 0.75,
              }}
            >
              {group.context}
            </Typography>

            {group.shortcuts.map((shortcut, si) => (
              <Box
                key={si}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 0.6,
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", color: "#333", fontFamily: "Mulish, sans-serif" }}>
                  {shortcut.description}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  {shortcut.keys.map((key, ki) => (
                    <Box component="kbd" key={ki} sx={keycapSx}>
                      {key}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcuts;
